/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: metadata-properties-modal-component
 *
 * Created by rwilliams on 5/24/17.
 */
'use strict';

function SubscriptionPropertiesModalController(
    _,
    $q,
    $timeout,
    uiGridConstants,
    DebugService,
    DynamicPropertiesService,
    PartnerEditService,
    SubscriptionsService,
    TerritoriesService,
    UtilsService
) {
    var self = this;
    self.availableTerritories = [];
    self.availableSeasonVolumes = [];
    self.availableServiceableLanguages = [];
    //	Object and properties that are set and hold the initial is-open value for each accordion in
    // 	the custom metadata section.  These control the open/close chevron on each accordion's header
    self.customMetaContainers = {
        mainContainer: true,
        mainSeasonVolumeContainer: false,
        seasonContainers: [],
        volumeContainers: []
    };

    self.isMovieSpecial = false;
    var volumeSeasonGridApiPromise = $q.defer();
    var collaboratorGridApiPromise = $q.defer();
    var modalReadyPromise = $q.defer();

    var serviceableLanguageFlags = [
        'cCEnabled', 'subtitleEnabled', 'audioEnabled', 'artworkEnabled'
    ];

    //
    // Creates a new ServiceableLanguage object for the given
    // language.
    //
    const createServiceableLanguage = (language) => {
        return {
            language: language,
            cCEnabled: false,
            subtitleEnabled: false,
            audioEnabled: false,
            artworkEnabled: false,
        };
    };

    var serviceableLanguageUsed = function(serviceableLanguage) {
        return _.some(serviceableLanguageFlags, function(flagName) {
            return (serviceableLanguage[flagName] === true);
        });
    };

    var getServiceableLanguageIndex = function(serviceableLanguageList, language) {
        if (language) {
            return _.findIndex(serviceableLanguageList, {
                language: {id: language.id}
            });
        }
        return _.findIndex(serviceableLanguageList, function(serviceableLanguage) {
            return (!serviceableLanguage.language);
        });
    };

    //
    // Called when the serviceable languages array should be
    // rebuilt (specifically, during initialization and when
    // reverting).
    //
    const updateServiceableLanguages = () => {
        _.forEach(self.allAvailableLanguages.concat(undefined), (language) => {
            let subscriptionIndex = getServiceableLanguageIndex(self.subscription.serviceableLanguages, language);
            let serviceableLanguage;
            if (subscriptionIndex >= 0) {
                serviceableLanguage = self.subscription.serviceableLanguages[subscriptionIndex];
                // If language isn't used, don't keep this language
                if (!serviceableLanguageUsed(serviceableLanguage)) {
                    self.subscription.serviceableLanguages.splice(subscriptionIndex, 1);
                }
            } else {
                serviceableLanguage = createServiceableLanguage(language);
            }

            // Add language to visible list
            let gridIndex = getServiceableLanguageIndex(self.availableServiceableLanguages, language);
            if (gridIndex >= 0) {
                _.assign(self.availableServiceableLanguages[gridIndex], serviceableLanguage);
            } else {
                self.availableServiceableLanguages.push(serviceableLanguage);
            }
        });

        self.availableServiceableLanguages = _
            .chain(self.availableServiceableLanguages)
            .orderBy('language.name')
            .partition({language: {name: 'English'}})
            .flatten()
            .partition((serviceableLanguage) => !serviceableLanguage.language)
            .flatten()
            .value();

        // If nothing selected, implies serviceableLanguage grid "All" chosen (and in valid state),
        // so make array valid also
        if (self.subscription.serviceableLanguages) {
            self.hasNoLanguages = false;
        } else {
            self.subscription.serviceableLanguages = [];
            self.hasNoLanguages = self.batchUpdate;
        }
        $timeout(self.editorForm.$setPristine);
    };

    this.onServiceableLanguagesChanged = (validServiceableLanguages) => {
        self.subscription.serviceableLanguages = validServiceableLanguages;
        if (!_.isNull(validServiceableLanguages)) {
            self.hasNoLanguages = false;
        }
        self.editorForm.$setDirty();
    };

    var getRoles = function (videoRoles) {
        var roles = [];
        _.forEach(videoRoles, function (videoRole) {
            if (videoRole.role.name !== 'Main Video') {
                roles.push(videoRole.role);
            }
        });

        return roles;
    };

    var getCrewMemberRolesAsStrings = function (crewMemberRoles) {
        var roles = [];
        _.forEach(crewMemberRoles, function (crewMemberRole) {
            roles.push(crewMemberRole.name);
        });
        return roles;
    };

    var getCrewMemberRoles = function () {
        var roleNames = DynamicPropertiesService.getPropValueAsList('package.crewMemberRoles');
        var crewMemberRoles = [];

        crewMemberRoles.push({name: 'Actor'});

        _.forEach(roleNames, function (roleName) {
            crewMemberRoles.push({name: roleName});
        });

        return crewMemberRoles;
    };

    var selectSeasonVolumeRows = function () {
        volumeSeasonGridApiPromise.promise.then(function () {
            var volumeSeasons = (self.subscription.seasonSubscription) ? self.subscription.seasons : self.subscription.volumes;
            var seasonOrVolume = (self.subscription.seasonSubscription) ? 'season' : 'volume';
            self.volumeSeasonGridApi.grid.modifyRows(self.volumeSeasonGridOptions.data);

            _.forEach(volumeSeasons, (volumeSeason) => {
                let row = _.find(self.volumeSeasonGridOptions.data, {
                    [seasonOrVolume]: {
                        id: volumeSeason[seasonOrVolume].id
                    }
                });
            self.volumeSeasonGridApi.selection.selectRow(row);
            });
            self.volumeSeasonGridApi.grid.modifyRows(self.volumeSeasonGridOptions.data);
        });
    };

    var selectCollaboratorRoles = function () {
        if (!self.subscription.actorsRequired) {
            collaboratorGridApiPromise.promise.then(function () {
                _.forEach(self.collaboratorGridOptions.data, function (row) {
                    _.forEach(self.subscription.requiredCrewRoles, function (crewRole) {
                        if (row.name === crewRole) {
                            self.collaboratorGridApi.grid.modifyRows(self.collaboratorGridOptions.data);
                            self.collaboratorGridApi.selection.selectRow(row);
                        }
                    });
                });
            });
        }
    };

    var setDefaultData = function (data) {
        if (!self.batchUpdate) {
            self.collaboratorGridOptions.data = getCrewMemberRoles();
            self.availableVideoRoles = data.contentCategory ? getRoles(data.contentCategory.videoRoles) : [];

            switch (self.content.type) {
                case 'Series':
                    var subTypeName = self.subscription.seasonSubscription ? 'seasons' : 'volumes';
                    var seasonOrVolume = self.subscription.seasonSubscription ? 'season' : 'volume';

                    self.availableSeasonVolumes = _.map(self.content[subTypeName], (seasonVolume) => {
                        let result = _.find(self.subscription[subTypeName], {
                            [seasonOrVolume]: {
                                id: seasonVolume.id
                            }
                        });
                        if (!result) {
                            result = {
                                [seasonOrVolume]: seasonVolume
                            };
                        }
                        return result;
                    });
                    self.setSeasonVolumeGridData(seasonOrVolume);

                    self.allSeasonVolume = (!_.size(self.subscription[subTypeName]));
                    self.allCollaborators = self.subscription.actorsRequired || false;
                    break;

                case 'Movie':
                    self.allCollaborators = true;
                    break;
            }
        }
    };

    var setSubscriptionData = function () {
        if (!self.batchUpdate) {
            self.subscription.artwork = _.sortBy(self.subscription.artwork, ['artworkAcceptProfile.name']);
            self.artworkGridOptions.data = self.subscription.artwork;

            volumeSeasonGridApiPromise.resolve();
            collaboratorGridApiPromise.resolve();
        }

        if (self.subscription.deliveryEligibilityExpression) {
            self.deliveryEligibility = true;
        }

        updateServiceableLanguages();
    };

    self.showSeasonVolumeContainer = function (seasonVolume) {
        return seasonVolume.hasOwnProperty('id');
    };

    self.isSeasonVolumeMetaValid = function () {
        var metaValid = false;
        if (self.subscription && !self.batchUpdate &&
            _.get(self.subscription, 'content.type') !== 'Movie/Special'
        ) {
            var volumeSeasons = (self.subscription.seasonSubscription) ? self.subscription.seasons : self.subscription.volumes;
            metaValid = _.some(volumeSeasons, (seasonVolume) => {
                return seasonVolume.id;
            });
        }
        return metaValid;
    };

    var fixupMetadataProperties = function () {
        // Add an isExpression field to the subscription metadata
        // properties objects for radio button functionality
        var collection = self.subscription.seasonSubscription ? self.subscription.seasons : self.subscription.volumes;

        if ((!self.batchUpdate || self.isPartner) && self.subscription.metadata.properties) {
            _.forEach(self.partnerMetadata, function (property) {
                // check if property.name exists in self.subscription.metadata.properties
                if (!self.subscription.metadata.properties.hasOwnProperty(property.name)) {
                    console.log('property not found: ' + property.name);
                } else {
                    var targetProp = self.subscription.metadata.properties[property.name];
                    var excludedTypes = ['TextInput', 'TextArea', 'CheckboxList', 'DropDownList', 'List'];
                    if (!_.includes(excludedTypes, property.uiControlHint)) {
                        targetProp.isExpression = !targetProp.hasOwnProperty('value');
                    }
                    _.forEach(collection, function (seasonOrVolume) {
                        if (seasonOrVolume.metadata) {
                            var targetSeasonVolumeProp = seasonOrVolume.metadata.properties[property.name];
                            if (!_.includes(excludedTypes, property.uiControlHint)) {
                                targetSeasonVolumeProp.isExpression = !targetSeasonVolumeProp.hasOwnProperty('value');
                            }
                        }
                    });
                }
            });
        }

        // 	Set the initial is-open value for all the season and volume accordions in order
        //	to control the open/close chevron on each custom metadata accordion header
        _.forEach(collection, function (seasonOrVolume) {
            var seasonVolumeObj = {};
            var seasonVolumeName = self.subscription.seasonSubscription ? seasonOrVolume.season.name : seasonOrVolume.volume.name;

            var container = self.subscription.seasonSubscription ? 'seasonContainers' : 'volumeContainers';

            seasonVolumeObj[seasonVolumeName] = false;
            self.customMetaContainers[container].push(seasonVolumeObj);
        });

    };

    var isHomogeneousContent = function () {
        // Iterate an array of subscriptions and return true if
        // they all share the same content
        var contentIds = _.map(self.subscriptions, 'content.id');
        var uniqueContentIds = _.uniq(contentIds);
        return uniqueContentIds.length === 1;
    };

    //
    // In batch update, we can default the modal properties
    // to a specific value when all of the selected
    // subscriptions share the same value.
    //
    // These functions attempt to set the default value based on
    // that.
    //

    var setDefaultChangeSetValue = function (changeSet, propName) {
        var i = 0;
        var sub;

        do {
            sub = self.subscriptions[i];
            if (sub.hasOwnProperty(propName) && !changeSet.hasOwnProperty(propName)) {
                changeSet[propName] = sub[propName];
            } else if (!sub.hasOwnProperty(propName) ||
                UtilsService.compareBasic(changeSet[propName], sub[propName]) !== 0) {
                delete changeSet[propName];
                break;
            }
            ++i;
        } while (i < self.subscriptions.length);
    };

    var copyServiceableLanguagesWithoutId = function (serviceableLanguages) {
        var results = [];
        for (var i in serviceableLanguages) {
            results.push(_.omit(serviceableLanguages[i], 'id'));
        }
        return results;
    };

    var setDefaultLanguagesValue = function (changeSet) {
        var languagesValue = copyServiceableLanguagesWithoutId(self.subscriptions[0].serviceableLanguages);
        for (var i = 1; i < self.subscriptions.length; ++i) {
            var languages = copyServiceableLanguagesWithoutId(self.subscriptions[i].serviceableLanguages);
            if (UtilsService.compareBasic(languagesValue, languages) !== 0) {
                return;
            }
        }
        changeSet.serviceableLanguages = languagesValue;
    };

    var setDefaultMetadataPropertyValue = function (changeSet, propName) {
        var i = 0;
        var sub;
        var propValue = null;

        do {
            sub = self.subscriptions[i];

            // Found a few of these in the database
            if (!sub.metadata || !sub.metadata.properties) {
                console.warn('Subscription has no metadata and/or no metadata properties:');
                console.warn('  -> ' + sub.partner.name + ' / ' + sub.content.name + ' / ' + sub.category.name);
                return;
            }

            if (!propValue) {
                // first iteration - grab metadata property value
                // of first subscription in list
                propValue = sub.metadata.properties[propName];
            } else if (UtilsService.compareBasic(propValue, sub.metadata.properties[propName]) !== 0) {
                // "this" subscription's value for the property
                // doesn't match previous subscriptions, so assign
                // no default in the change set
                return;
            }
            ++i;
        } while (i < self.subscriptions.length);

        if (propValue.value) {
            changeSet.metadata.properties[propName].value = propValue.value;
        } else if (propValue.expression) {
            changeSet.metadata.properties[propName].expression = propValue.expression;
        }
    };

    var createDefaultChangeSet = function () {
        //
        // Creates the initial, default subscription "change set"
        // for batch updating. By default, we don't actually want
        // to change anything. Rather by setting these values, we
        // set the default values that the different property
        // components in the modal.
        //
        // For instance, if all of the subscriptions in the batch
        // update have the same preroll, that preroll will be
        // selected in the modal.
        //

        var batchUpdateFields = ['enabled', 'preRoll', 'preRollSeasonVolume', 'postRoll', 'postRollSeasonVolume',
            'deliveryEligibilityExpression', 'territories', 'allCollaborators'];
        var partnerOnlyBatchUpdateFields = ['nielsenTemplate', 'watermark', 'watermarkSeasonVolume',
            'watermarkFadeInSeconds', 'watermarkFadeOutSeconds', 'watermarkOffsetSeconds', 'dueDateTemplate'];
        if (self.isPartner) {
            batchUpdateFields = _.concat(batchUpdateFields, partnerOnlyBatchUpdateFields);
        }

        var changeSet = {};
        _.forEach(batchUpdateFields, function (fieldName) {
            setDefaultChangeSetValue(changeSet, fieldName);
        });
        setDefaultLanguagesValue(changeSet);

        if (self.isPartner) {
            // Partner custom metadata cannot be batch-updated
            // from the content categories screen.
            changeSet.metadata = {properties: {}};
            _.forEach(self.partnerMetadata, function (prop) {
                changeSet.metadata.properties[prop.name] = {};
                setDefaultMetadataPropertyValue(changeSet, prop.name);
            });
        }
        return changeSet;
    };

    var getData = function () {
        DynamicPropertiesService.queryForProperties()
            .then(() => {
                return SubscriptionsService.getCollectionData(
                    self.batchUpdate,
                    self.isPartner,
                    self.partner,
                    self.content,
                    self.category,
                    self.subscriptions);
            }).then(function (collectionData) {
                if (collectionData.partnerMetadata !== undefined) {
                    self.partnerMetadata = _.sortBy(
                        collectionData.partnerMetadata,
                        [function (metaItem) {
                            return metaItem.displayIndex;
                        }]
                    );
                }
                if (collectionData.partner !== undefined) {
                    self.partner = collectionData.partner;

                    PartnerEditService.getDueDateTemplatesForPartner(self.partner.id, false)
                        .then((dueDateTemplates) => {
                            _.set(self, 'availableDueDateTemplates', dueDateTemplates);
                        });

                    PartnerEditService.getNielsenTemplatesForPartner(self.partner.id)
                        .then((nielsenTemplates) => {
                            _.set(self, 'availableNielsenTemplates', nielsenTemplates);
                        });
                }

                self.availablePrerolls = _.concat(
                    collectionData.content !== undefined ? collectionData.content.preRolls : [],
                    collectionData.network !== undefined ? collectionData.network.preRolls : []);
                self.availablePostrolls = _.concat(
                    collectionData.content !== undefined ? collectionData.content.postRolls : [],
                    collectionData.network !== undefined ? collectionData.network.postRolls : []);
                self.availableWatermarks = _.concat(
                    collectionData.content !== undefined ? collectionData.content.watermarks : [],
                    collectionData.network !== undefined ? collectionData.network.watermarks : []);
                self.artworks = _.concat(
                    collectionData.content !== undefined ? collectionData.content.artwork : [],
                    collectionData.network !== undefined ? collectionData.network.artwork : []);
                self.availableBreakPrePostRolls = _.concat(
                    collectionData.content !== undefined ? collectionData.content.breakPrePostRolls : [],
                    collectionData.network !== undefined ? collectionData.network.breakPrePostRolls : []);
                self.availableIntermediateTemplates = collectionData.intermediateTemplateData;
                self.artworkAcceptProfiles = collectionData.artworkAcceptProfiles;

                self.allAvailableLanguages = collectionData.languages;

                self.subscription = self.batchUpdate ? createDefaultChangeSet() : self.subscriptions[0];

                if (collectionData.content !== undefined) {
                    self.content = collectionData.content;
                }

                if (collectionData.contentCategory !== undefined) {
                    self.contentCategory = collectionData.contentCategory;
                    setDefaultData(self.contentCategory);
                }

                setSubscriptionData();
                self.setArtworkGridColumns(self.content);
                fixupMetadataProperties();

                modalReadyPromise.resolve();
                self.loadingIndicator.show = false;
            });

        // get the available territories
        TerritoriesService.getAllTerritories({fetchLimit: 0, includeHidden: false}).then((territories) => {
            // Place USA at the top of the list
            const territoriesWithUsaFirst = _.chain(territories)
                .partition({name: 'USA'})
                .flatten()
                .value();
            _.set(self, 'availableTerritories', territoriesWithUsaFirst);
        });

    };

    var setRequiredCollaborators = function () {
        if (self.allCollaborators) {
            self.subscription.actorsRequired = true;
            return getCrewMemberRolesAsStrings(self.collaboratorGridOptions.data);
        }
        self.subscription.actorsRequired = false;
        return getCrewMemberRolesAsStrings(self.collaboratorGridApi.selection.getSelectedRows());
    };

    self.dropDownSelectLabelFunction = function (item, field) {
        return _.get(item, field);
    };

    self.typeChange = function () {
        setDefaultData(self.contentCategory);
        selectSeasonVolumeRows();
    };

    self.toggleRows = function (event, grid) {
        if (grid.api.selection.selectAll) {
            grid.api.selection.clearSelectedRows(event);
            grid.api.selection.selectAll = false;
        } else {
            grid.api.selection.selectAllVisibleRows(event);
            grid.api.selection.selectAll = true;
        }
    };

    self.enabledVideoRolesUpdated = function (context, newEnabledEntities) {
        self.subscription.videoRoles = newEnabledEntities;
        self.editorForm.$setDirty();
    };

    self.loadingIndicator = {
        backdrop: true,
        show: false,
        title: 'Working',
        message: 'Please wait...'
    };

    self.$onInit = function () {
        self.loadingIndicator.show = true;

        // Passed into modal from grid
        self.isPartner = self.resolve.isPartner;
        self.subscriptions = self.resolve.subscriptions;
        self.batchUpdate = self.subscriptions.length > 1;
        self.isChangingLanguages = !self.batchUpdate;

        self.partnerMetadata = null;

        self.partner = null;
        self.content = null;
        self.category = null;

        self.partner = !self.batchUpdate || self.isPartner ? self.subscriptions[0].partner : null;
        self.content = !self.batchUpdate || (!self.isPartner || isHomogeneousContent()) ? self.subscriptions[0].content : null;
        self.category = !self.batchUpdate || !self.isPartner ? self.subscriptions[0].category : null;

        self.deliveryEligibility = false;
        self.allSeasonVolume = true;
        self.allCollaborators = false;

        getData();
    };

    self.isOkEnabled = function() {
        var languagesValid = !self.isChangingLanguages || !!_.get(self.subscription, 'serviceableLanguages');
        return (self.editorForm.$dirty && languagesValid);
    };

    self.onOk = function () {
        if (self.subscription.deliveryEligibilityExpression && !self.deliveryEligibility) {
            self.subscription.deliveryEligibilityExpression = '';
        }
        if (!self.isChangingLanguages) {
            delete self.subscription.serviceableLanguages;
        }

        if (!self.batchUpdate) {
            if (self.subscription.content.type !== 'Movie/Special') {
                var seasonsVolumes = (self.allSeasonVolume) ? [] : self.volumeSeasonGridApi.selection.getSelectedRows();
                if (self.subscription.seasonSubscription) {
                    self.subscription.seasons = seasonsVolumes;
                    self.subscription.volumes = [];
                } else {
                    self.subscription.seasons = [];
                    self.subscription.volumes = seasonsVolumes;
                }
            }

            _.remove(self.subscription.artwork, function (artworkAsset) {
                return !artworkAsset.artwork.id && !artworkAsset.artworkAcceptProfile.id;
            });
            if (self.content.type !== 'Ad/PreRoll') {
                self.subscription.requiredCrewRoles = setRequiredCollaborators();
            }
        }

        var customMeta = self.subscription.metadata;
        if (customMeta && customMeta.properties) {
            for (var metadataItem in customMeta.properties) {
                if (customMeta.properties.hasOwnProperty(metadataItem)) {
                    if (customMeta.properties[metadataItem].hasOwnProperty('isExpression')) {
                        if (customMeta.properties[metadataItem].isExpression && customMeta.properties[metadataItem].hasOwnProperty('value')) {
                            delete customMeta.properties[metadataItem].value;
                        }
                        if (!customMeta.properties[metadataItem].isExpression && customMeta.properties[metadataItem].hasOwnProperty('expression')) {
                            delete customMeta.properties[metadataItem].expression;
                        }
                        delete customMeta.properties[metadataItem].isExpression;
                    }
                }
            }
        }

        self.close({$value: self.subscription});
    };

    self.addArtwork = function () {
        var artworkAsset = {
            artwork: {},
            artworkAcceptProfile: {},
            artworkSeasonVolume: false
        };

        self.artworkGridOptions.data.push(artworkAsset);
        self.editorForm.$setDirty();
    };

    self.removeArtwork = function () {
        var selectedRows = self.artworkGridApi.selection.getSelectedRows();

        if (selectedRows.length > 0) {
            _.pullAll(self.artworkGridOptions.data, selectedRows);
            self.editorForm.$setDirty();
        }
    };

    // This is currently only used for the Artwork "Use
    // Season/Volume Artwork" column checkboxes in the artwork
    // grid.
    self.checkMarkCellClicked = function (row, event) {
        event.stopPropagation();
        row.isSelected = true;
        row.entity.artworkSeasonVolume = !row.entity.artworkSeasonVolume;
        self.editorForm.$setDirty();
    };

    //===============//
    // Grids Columns //
    //===============//

    var seasonColDefs = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: 30,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            headerCellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/season-check-mark-header-cell-template.html',
            cellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/season-check-mark-cell-template.html'
        },
        {
            field: 'season.name',
            name: 'Season',
            width: 160,
            sortable: false,
            enableColumnMenu: false,
            sort: {
                direction: uiGridConstants.ASC,
                priority: 0
            }
        },
        {
            field: 'season.startDate',
            name: 'Start Date',
            width: 140,
            sortable: false,
            enableColumnMenu: false,
            cellFilter: 'amDateFormat: \'MMMM DD, YYYY\''
        },
        {
            field: 'season.endDate',
            name: 'End Date',
            width: 140,
            sortable: false,
            enableColumnMenu: false,
            cellFilter: 'amDateFormat: \'MMMM DD, YYYY\''
        },
        {
            field: 'modified',
            name: 'Modified',
            sortable: false,
            enableColumnMenu: false
        }
    ];

    var volumeColDefs = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: 30,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            headerCellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/volume-check-mark-header-cell-template.html',
            cellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/volume-check-mark-cell-template.html'
        },
        {
            field: 'volume.name',
            name: 'Volume',
            width: 160,
            sortable: false,
            enableColumnMenu: false,
            sort: {
                direction: uiGridConstants.ASC,
                priority: 0
            }
        },
        {
            field: 'volume.startDate',
            name: 'Start Date',
            width: 140,
            sortable: false,
            enableColumnMenu: false,
            cellFilter: 'amDateFormat: \'MMMM DD, YYYY\''
        },
        {
            field: 'volume.endDate',
            name: 'End Date',
            width: 140,
            sortable: false,
            enableColumnMenu: false,
            cellFilter: 'amDateFormat: \'MMMM DD, YYYY\''
        },
        {
            field: 'modified',
            name: 'Modified',
            sortable: false,
            enableColumnMenu: false
        }
    ];
    var dropdownSortByName = (a, b) => {
        return _.toLower(_.get(a, 'name')).localeCompare(_.toLower(_.get(b, 'name')));
    };
    var artworkGridColumns = [
        {
            field: 'checkMark',
            name: 'checkMarkRowHeaderCol',
            width: 30,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            headerCellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/check-mark-header-template.html'
        },
        {
            field: 'artwork',
            name: 'Artwork',
            sortable: true,
            enableSorting: true,
            sort: {
                direction: uiGridConstants.ASC,
                priority: 0
            },
            sortingAlgorithm: dropdownSortByName,
            enableColumnMenu: false,
            width: 330,
            cellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/artwork-cell-template.html'
        },
        {
            field: 'artworkAcceptProfile',
            name: 'Artwork MAP',
            width: 455,
            sortable: true,
            enableSorting: true,
            sort: {
                direction: uiGridConstants.ASC,
                priority: 1
            },
            sortingAlgorithm: dropdownSortByName,
            enableColumnMenu: false,
            cellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/artwork-accept-profile-cell-template.html'
        },
        {
            field: 'artworkSeasonVolume',
            name: 'S/V Art',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-boolean-template.html'
        }
    ];

    var collaboratorGridColumns = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: 30,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/collaborator-check-mark-cell-template.html',
            headerCellTemplate: 'components/modal/subscription-properties-modal/cellTemplates/collaborator-check-mark-header-cell-template.html'
        },
        {
            field: 'name',
            name: 'Collaborator',
            sortable: false,
            enableColumnMenu: false
        }
    ];

    self.setSeasonVolumeGridData = function (seasonOrVolume) {
        self.volumeSeasonGridOptions.data = self.availableSeasonVolumes;
        if (seasonOrVolume === 'season') {
            self.volumeSeasonGridOptions.columnDefs = seasonColDefs;
        } else {
            self.volumeSeasonGridOptions.columnDefs = volumeColDefs;
        }
    };

    self.setArtworkGridColumns = function (content) {
        if (content && content.type === 'Movie/Special') {
            artworkGridColumns.splice(3, 1);
            self.isMovieSpecial = true;
        }
        self.artworkGridOptions.columnDefs = artworkGridColumns;
    };

    //===============//
    // Grids Options //
    //===============//

    self.volumeSeasonGridOptions = {
        selectionRowHeaderColName: 'checkMarkRowHeaderCol',
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableFullRowSelection: true,
        enableSorting: false,
        rowHeight: 26,
        multiSelect: true,
        minRowsToShow: 10,
        enableVerticalScrollbar: 2,
        columnDefs: seasonColDefs,
        onRegisterApi: function (gridApi) {
            self.volumeSeasonGridApi = gridApi;

            selectSeasonVolumeRows();
        }
    };

    self.artworkGridOptions = {
        selectionRowHeaderColName: 'checkMarkRowHeaderCol',
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableFullRowSelection: true,
        enableSorting: true,
        rowHeight: 26,
        multiSelect: true,
        minRowsToShow: 4,
        enableVerticalScrollbar: 2,
        onRegisterApi: function (gridApi) {
            self.artworkGridApi = gridApi;
        }
    };

    self.collaboratorGridOptions = {
        selectionRowHeaderColName: 'checkMarkRowHeaderCol',
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableFullRowSelection: true,
        enableSorting: false,
        rowHeight: 26,
        multiSelect: true,
        minRowsToShow: 5,
        enableVerticalScrollbar: 2,
        columnDefs: collaboratorGridColumns,
        onRegisterApi: function (gridApi) {
            self.collaboratorGridApi = gridApi;
            modalReadyPromise.promise.then(function () {
                selectCollaboratorRoles();
            });
        }
    };
}

angular.module('prism-ui.components').component('subscriptionPropertiesModal', {
    templateUrl: 'components/modal/subscription-properties-modal/subscription-properties-modal-template.html',
    controller: SubscriptionPropertiesModalController,
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&'
    }
});
